import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios to send credentials
axios.defaults.withCredentials = true;

// API helper to format error messages
export function formatApiError(error) {
  const detail = error.response?.data?.detail;
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Auth API
export const authApi = {
  forgotPassword: (email) => axios.post(`${API_URL}/api/auth/forgot-password`, { email }),
  resetPassword: (token, new_password) => axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password }),
};

// Public content API
export const contentApi = {
  getLandingContent: () => axios.get(`${API_URL}/api/content/landing`),
  getHomepageContent: () => axios.get(`${API_URL}/api/content/homepage`),
  getAboutContent: () => axios.get(`${API_URL}/api/content/about`),
  getProgrammeSettings: () => axios.get(`${API_URL}/api/content/programme`),
  getLiveSessions: () => axios.get(`${API_URL}/api/content/live-sessions`),
  // These resolve the real Zoom/recording URL server-side on each click —
  // the raw link is never present in the getLiveSessions response.
  getJoinUrl: (day) => `${API_URL}/api/content/live-sessions/${day}/join`,
  getReplayUrl: (day) => `${API_URL}/api/content/live-sessions/${day}/replay`,
  fetchReplayUrl: (day) => axios.get(`${API_URL}/api/content/live-sessions/${day}/replay`),
  getBlogPosts: () => axios.get(`${API_URL}/api/content/blog`),
  getBlogPost: (slug) => axios.get(`${API_URL}/api/content/blog/${slug}`),
};

// Payment API
export const paymentApi = {
  // Guest checkout — no auth required, collects name/email/phone
  initiateRazorpayGuest: (data) =>
    axios.post(`${API_URL}/api/payments/razorpay/initiate`, data),
  // Authenticated Razorpay order (used by already-logged-in users)
  createRazorpayOrder: (packageId, originUrl) =>
    axios.post(`${API_URL}/api/payments/checkout/razorpay`, {
      package_id: packageId,
      payment_gateway: 'razorpay',
      origin_url: originUrl
    }),
  verifyRazorpay: (data) =>
    axios.post(`${API_URL}/api/payments/razorpay/verify`, data),
  // Stripe — kept for future use, not exposed in UI
  createStripeCheckout: (packageId, originUrl) =>
    axios.post(`${API_URL}/api/payments/checkout/stripe`, {
      package_id: packageId,
      payment_gateway: 'stripe',
      origin_url: originUrl
    }),
  getPaymentStatus: (sessionId) =>
    axios.get(`${API_URL}/api/payments/checkout/status/${sessionId}`),
};

// Video API
export const videoApi = {
  getMyVideos: () => axios.get(`${API_URL}/api/videos/my-videos`),
};

// Admin API
export const adminApi = {
  // Image upload — get presigned S3 URL, then PUT the blob directly to S3
  getUploadUrl: () => axios.post(`${API_URL}/api/admin/upload-url`),

  // Content
  getContent: () => axios.get(`${API_URL}/api/admin/content`),
  saveContent: (data) => axios.post(`${API_URL}/api/admin/content`, data),
  deleteContent: (sectionId) => axios.delete(`${API_URL}/api/admin/content/${sectionId}`),
  
  // Curriculum
  getCurriculum: () => axios.get(`${API_URL}/api/admin/curriculum`),
  saveCurriculum: (data) => axios.post(`${API_URL}/api/admin/curriculum`, data),
  deleteCurriculumDay: (day) => axios.delete(`${API_URL}/api/admin/curriculum/${day}`),
  deleteCurriculumWeek: (week) => axios.delete(`${API_URL}/api/admin/curriculum/week/${week}`),
  
  // Testimonials
  getTestimonials: () => axios.get(`${API_URL}/api/admin/testimonials`),
  createTestimonial: (data) => axios.post(`${API_URL}/api/admin/testimonials`, data),
  updateTestimonial: (id, data) => axios.put(`${API_URL}/api/admin/testimonials/${id}`, data),
  deleteTestimonial: (id) => axios.delete(`${API_URL}/api/admin/testimonials/${id}`),
  
  // FAQs
  getFaqs: () => axios.get(`${API_URL}/api/admin/faqs`),
  createFaq: (data) => axios.post(`${API_URL}/api/admin/faqs`, data),
  updateFaq: (id, data) => axios.put(`${API_URL}/api/admin/faqs/${id}`, data),
  deleteFaq: (id) => axios.delete(`${API_URL}/api/admin/faqs/${id}`),
  
  // Instructors
  getInstructors: () => axios.get(`${API_URL}/api/admin/instructors`),
  createInstructor: (data) => axios.post(`${API_URL}/api/admin/instructors`, data),
  updateInstructor: (id, data) => axios.put(`${API_URL}/api/admin/instructors/${id}`, data),
  deleteInstructor: (id) => axios.delete(`${API_URL}/api/admin/instructors/${id}`),
  
  // Videos
  getVideos: () => axios.get(`${API_URL}/api/admin/videos`),
  createVideo: (data) => axios.post(`${API_URL}/api/admin/videos`, data),
  updateVideo: (id, data) => axios.put(`${API_URL}/api/admin/videos/${id}`, data),
  deleteVideo: (id) => axios.delete(`${API_URL}/api/admin/videos/${id}`),

  // Live Sessions
  getLiveSessions: () => axios.get(`${API_URL}/api/admin/live-sessions`),
  createLiveSession: (data) => axios.post(`${API_URL}/api/admin/live-sessions`, data),
  updateLiveSession: (id, data) => axios.put(`${API_URL}/api/admin/live-sessions/${id}`, data),
  deleteLiveSession: (id) => axios.delete(`${API_URL}/api/admin/live-sessions/${id}`),
  
  // Settings
  getSettings: () => axios.get(`${API_URL}/api/admin/settings`),
  saveSettings: (data) => axios.post(`${API_URL}/api/admin/settings`, data),
  
  // Users
  getUsers: () => axios.get(`${API_URL}/api/admin/users`),
  seedTestUser: () => axios.post(`${API_URL}/api/admin/users/seed-test-user`),
  toggleRegistration: (userId) => axios.put(`${API_URL}/api/admin/users/${userId}/registration`),
  toggleDisable: (userId) => axios.put(`${API_URL}/api/admin/users/${userId}/disable`),
  deleteUser: (userId) => axios.delete(`${API_URL}/api/admin/users/${userId}`),
  bulkInvite: (users) => axios.post(`${API_URL}/api/admin/users/bulk-invite`, { users }),
  getRegistrations: () => axios.get(`${API_URL}/api/admin/registrations`),

  // Progress reporting
  getProgressOverview: () => axios.get(`${API_URL}/api/admin/progress/overview`),
  getUserProgress: (userId) => axios.get(`${API_URL}/api/admin/progress/user/${userId}`),

  // Email broadcast
  emailBroadcast: (subject, body) => axios.post(`${API_URL}/api/admin/email/broadcast`, { subject, body }),

  // Blog
  getBlogs: () => axios.get(`${API_URL}/api/admin/blog`),
  getBlog: (id) => axios.get(`${API_URL}/api/admin/blog/${id}`),
  createBlog: (data) => axios.post(`${API_URL}/api/admin/blog`, data),
  updateBlog: (id, data) => axios.put(`${API_URL}/api/admin/blog/${id}`, data),
  deleteBlog: (id) => axios.delete(`${API_URL}/api/admin/blog/${id}`),
};

// Public contact form
export const contactApi = {
  submit: (data) => axios.post(`${API_URL}/api/contact`, data),
};
